"use client"

import { useState, useEffect, useCallback } from "react"
import type { ConnectorOption } from "../types/sync-flow"
import { DEFAULT_CONNECTORS, STORAGE_KEYS } from "../constants/sync-flow"
import { useLocalStorage } from "./useLocalStorage"

export const useConnectorSelection = () => {
  const [selectedConnector, setSelectedConnector] = useState<string | null>(null)
  const [connectors, setConnectors] = useState<ConnectorOption[]>([])
  const [loading, setLoading] = useState(true)

  const { getItem, setItem, clearFlowData } = useLocalStorage()

  const initializeConnectors = useCallback(async () => {
    try {
      setLoading(true)

      // Clear previous flow data
      clearFlowData()

      setConnectors(DEFAULT_CONNECTORS)

      // Check for previous selection
      const savedConnector = getItem(STORAGE_KEYS.SELECTED_CONNECTOR)
      if (savedConnector) {
        setSelectedConnector(savedConnector)
      }
    } catch (error) {
      console.error("Failed to load connectors:", error)
    } finally {
      setLoading(false)
    }
  }, [getItem, clearFlowData])

  const selectConnector = useCallback(
    (connectorId: string) => {
      setSelectedConnector(connectorId)
      setItem(STORAGE_KEYS.SELECTED_CONNECTOR, connectorId)
    },
    [setItem],
  )

  const proceedToNext = useCallback(() => {
    if (selectedConnector) {
      setItem(STORAGE_KEYS.CONNECTOR_FLOW_STEP, "1")
      return true
    }
    return false
  }, [selectedConnector, setItem])

  useEffect(() => {
    initializeConnectors()
  }, [initializeConnectors])

  return {
    selectedConnector,
    connectors,
    loading,
    selectConnector,
    proceedToNext,
  }
}
